/**
 * Mapbox GL needs a WebGL context, and not every browser can give it one: headless and cloud
 * browsers (AI assistants, crawlers), VMs and remote desktops without a GPU, browsers with
 * hardware acceleration switched off, and some older devices. Mapbox GL v1 answers that by
 * throwing synchronously from `new mapboxgl.Map()` — "Failed to initialize WebGL".
 *
 * Both map components used to call the constructor bare, inside a React lifecycle. A throw
 * there climbs to the route error boundary, which replaces the whole page with "Something
 * went wrong" — so a missing map took the search results, the search box and the listing page
 * down with it. Every construction now goes through here and never throws: the caller gets a
 * map or `null`, and decides what to show instead.
 */

const warn = (message, error) => {
  if (typeof console !== 'undefined' && console.warn) {
    console.warn(`[map] ${message}`, error || '');
  }
};

/**
 * Whether Mapbox GL is loaded and reports that this browser can run it.
 *
 * Uses `mapboxgl.supported()`, which probes for a WebGL context on a scratch canvas without
 * building a map. Returns false while the library is still loading; callers that can wait
 * should keep listening for `mapboxgl:ready` rather than treating that as unsupported.
 */
export const isMapboxGlSupported = () => {
  if (typeof window === 'undefined' || !window.mapboxgl) return false;
  // Removed in v2, where the constructor is the only check. try/catch below covers that.
  if (typeof window.mapboxgl.supported !== 'function') return true;
  try {
    return window.mapboxgl.supported() !== false;
  } catch (error) {
    warn('mapboxgl.supported() threw; treating WebGL as unavailable.', error);
    return false;
  }
};

/**
 * Construct a Mapbox GL map, or return `null` when this browser cannot run one.
 *
 * `supported()` is the cheap pre-check, but it is not sufficient on its own — a browser can
 * pass it and still fail to create a context (too many live contexts, a context that was just
 * lost), and v2 has no pre-check at all — so the constructor is guarded as well.
 *
 * @param {Object} options - the usual `mapboxgl.Map` options
 * @returns {Object|null}
 */
export const createMapboxMap = options => {
  if (!isMapboxGlSupported()) {
    warn('WebGL is not available in this browser; continuing without a map.');
    return null;
  }
  try {
    return new window.mapboxgl.Map(options);
  } catch (error) {
    warn('Mapbox GL could not initialise; continuing without a map.', error);
    return null;
  }
};
